// 6sense Integration Component with Configuration

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, Settings, RefreshCw, AlertCircle, CheckCircle, Zap } from 'lucide-react';
import { sixSenseApi, sixSenseQueries, setupSixSenseTokens, type SixSenseEnrichmentRequest } from '../../services/api/sixsense.api';

interface SixSenseIntegrationProps {
  className?: string;
  onEnrichmentComplete?: (data: any) => void;
  onError?: (error: string) => void;
}

interface SixSenseConfig {
  apiToken: string;
  baseUrl?: string;
}

export const SixSenseIntegration: React.FC<SixSenseIntegrationProps> = ({
  className = '',
  onEnrichmentComplete,
  onError,
}) => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [config, setConfig] = useState<SixSenseConfig>({ apiToken: '' });
  const [showConfig, setShowConfig] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testDomain, setTestDomain] = useState('');

  const queryClient = useQueryClient();

  // Check if tokens are configured
  useEffect(() => {
    const savedConfig = localStorage.getItem('sixsense-config');
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        setConfig(parsed);
        if (parsed.apiToken) {
          setupSixSenseTokens(parsed.apiToken, parsed.baseUrl);
          setIsConfigured(true);
        }
      } catch (error) {
        console.error('Failed to parse saved 6sense config:', error);
      }
    }
  }, []);

  // Test connection
  const {
    data: connectionTest,
    isLoading: connectionLoading,
    error: connectionError,
    refetch: testConnection,
  } = useQuery({
    ...sixSenseQueries.connection(),
    enabled: isConfigured,
    retry: 1,
  });

  // Test enrichment mutation
  const testEnrichmentMutation = useMutation({
    mutationFn: async () => {
      if (!testEmail && !testDomain) {
        throw new Error('Please provide either an email or domain for testing');
      }
      return sixSenseApi.enrichLead(testEmail, testDomain);
    },
    onSuccess: (data) => {
      onEnrichmentComplete?.(data);
      queryClient.invalidateQueries(['sixsense']);
    },
    onError: (error: any) => {
      onError?.(error.message || 'Failed to test enrichment');
    },
  });

  const handleSaveConfig = () => {
    try {
      localStorage.setItem('sixsense-config', JSON.stringify(config));
      if (config.apiToken) {
        setupSixSenseTokens(config.apiToken, config.baseUrl);
        setIsConfigured(true);
        setShowConfig(false);
        testConnection();
      }
    } catch (error) {
      onError?.('Failed to save configuration');
    }
  };

  const handleTestEnrichment = () => {
    testEnrichmentMutation.mutate();
  };

  if (!isConfigured) {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 ${className}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">6sense Integration</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Enrich leads with 6sense data</p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300">Setup Required</h3>
                <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                  To use 6sense integration, you need to provide your API token. You can get this from your 6sense platform settings.
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowConfig(true)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <Settings className="w-5 h-5" />
            <span>Configure 6sense</span>
          </button>

          {showConfig && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">6sense Configuration</h3>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API Token <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={config.apiToken}
                      onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
                      placeholder="your-6sense-api-token"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Base URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={config.baseUrl || ''}
                      onChange={(e) => setConfig({ ...config, baseUrl: e.target.value })}
                      placeholder="https://api.6sense.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="flex space-x-3 mt-6">
                  <button
                    onClick={() => setShowConfig(false)}
                    className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white py-2 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveConfig}
                    disabled={!config.apiToken}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors"
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Database className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">6sense Integration</h2>
              <div className="flex items-center space-x-2">
                {connectionTest && !connectionError ? (
                  <div className="flex items-center space-x-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span className="text-sm text-green-600 dark:text-green-400">
                      Connected successfully
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <AlertCircle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-red-600 dark:text-red-400">Connection error</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => testConnection()}
              disabled={connectionLoading}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${connectionLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowConfig(true)}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Settings className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Test Enrichment */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Test Enrichment
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <input
                type="text"
                value={testDomain}
                onChange={(e) => setTestDomain(e.target.value)}
                placeholder="example.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          <button
            onClick={handleTestEnrichment}
            disabled={(!testEmail && !testDomain) || testEnrichmentMutation.isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            {testEnrichmentMutation.isLoading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Zap className="w-5 h-5" />
            )}
            <span>
              {testEnrichmentMutation.isLoading ? 'Testing...' : 'Test Enrichment'}
            </span>
          </button>

          {testEnrichmentMutation.isSuccess && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-sm text-green-700 dark:text-green-400">
                  Enrichment test successful!
                </span>
              </div>
            </div>
          )}

          {testEnrichmentMutation.isError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-sm text-red-700 dark:text-red-400">
                  {testEnrichmentMutation.error?.message || 'Test failed'}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Configuration Modal */}
        {showConfig && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Update 6sense Configuration</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    API Token <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={config.apiToken}
                    onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
                    placeholder="your-6sense-api-token"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Base URL (Optional)
                  </label>
                  <input
                    type="url"
                    value={config.baseUrl || ''}
                    onChange={(e) => setConfig({ ...config, baseUrl: e.target.value })}
                    placeholder="https://api.6sense.com"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowConfig(false)}
                  className="flex-1 bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white py-2 px-4 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConfig}
                  disabled={!config.apiToken}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white py-2 px-4 rounded-lg transition-colors"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};